import React, { ChangeEvent, memo, useCallback, useRef, useState } from 'react'
import tw from 'twin.macro'
import { CircularProgress, Tooltip } from '@mui/material'
import { useSnackbar } from 'notistack'
import { AdminUpdateCustomer } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { Customer } from 'src/interfaces/customer'
import { fileToDataUrl } from 'src/utils/fileToDataUrl'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  customer: Customer
  // Without `partner_settings_manage` the PUT would be refused, so the tile is a plain image.
  isReadOnly: boolean
  onCustomerChanged(): void
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg']
const MAX_SIZE_BYTES = 10 * 1000 * 1000

/**
 * The company logo tile at the top-right of the customer card. A 64px well: the logo when the
 * customer has one, a lone upload glyph when it does not. Hovering (or focusing) a logo reveals
 * a pencil over it; either state opens the file picker, and a chosen file is PUT straight to
 * the customer — no form, no confirm — then the customer is refetched for its signed URL.
 */
const CustomerLogoUploaderComponent = ({ customer, isReadOnly, onCustomerChanged }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const hasLogo = Boolean(customer.logoUrl)
  const label = hasLogo ? 'Change company logo' : 'Upload company logo'

  const handleOpenPicker = useCallback(() => {
    if (isUploading) {
      return
    }

    inputRef.current?.click()
  }, [isUploading])

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]

      // Reset so picking the same file again still fires `change`.
      event.target.value = ''

      if (!file) {
        return
      }

      if (!ACCEPTED_TYPES.includes(file.type)) {
        snackbar?.enqueueSnackbar('Logo must be a PNG or JPG', { variant: 'error' })

        return
      }

      if (file.size > MAX_SIZE_BYTES) {
        snackbar?.enqueueSnackbar('Logo must be 10MB or smaller', { variant: 'error' })

        return
      }

      setIsUploading(true)

      try {
        const logo = await fileToDataUrl(file)
        const result = await asyncDispatchWithErrorToast(
          AdminUpdateCustomer.request({ customerId: customer.id, logo }),
        )

        if (result === null) {
          return
        }

        snackbar?.enqueueSnackbar('Company logo updated', { variant: 'success' })
        onCustomerChanged()
      } finally {
        setIsUploading(false)
      }
    },
    [asyncDispatchWithErrorToast, customer.id, onCustomerChanged, snackbar],
  )

  // Hooks above still run; only the rendering differs. A customer with no logo shows nothing —
  // an empty upload well would advertise an action this admin cannot take.
  if (isReadOnly) {
    if (!hasLogo) {
      return null
    }

    return (
      <div
        data-testid="admin-customer-logo"
        css={tw`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-solid border-shades-100 bg-shades-50`}
      >
        <img
          src={customer.logoUrl ?? undefined}
          alt=""
          css={tw`h-full w-full object-contain p-2`}
        />
      </div>
    )
  }

  return (
    <Tooltip title={label}>
      <button
        type="button"
        aria-label={label}
        aria-busy={isUploading}
        disabled={isUploading}
        onClick={handleOpenPicker}
        data-testid="admin-customer-logo-button"
        className="group"
        css={tw`relative flex h-16 w-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-solid border-shades-100 bg-shades-50 p-0 hover:border-shades-200 focus-visible:border-shades-200 disabled:cursor-default`}
      >
        {hasLogo ? (
          <img
            src={customer.logoUrl ?? undefined}
            alt=""
            css={tw`h-full w-full object-contain p-2`}
          />
        ) : (
          <GSIcon iconName="gs_cloud_upload" size={20} css={tw`text-shades-400`} />
        )}
        {hasLogo && !isUploading ? (
          // The edit affordance only shows on intent, so the logo reads as a logo at rest.
          <span
            css={tw`absolute inset-0 flex items-center justify-center bg-[rgba(255,255,255,0.8)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none`}
          >
            <GSIcon iconName="gs_pencil" size={20} css={tw`text-shades-1000`} />
          </span>
        ) : null}
        {isUploading ? (
          <span
            css={tw`absolute inset-0 flex items-center justify-center bg-[rgba(255,255,255,0.8)]`}
          >
            <CircularProgress size={20} />
          </span>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          hidden
          onChange={handleFileChange}
          data-testid="admin-customer-logo-input"
        />
      </button>
    </Tooltip>
  )
}

export const CustomerLogoUploader = memo(CustomerLogoUploaderComponent)
